import { queryAlgolia } from '../../../util/api';
import * as log from '../../../util/log';
import { algoliaListingFacets } from '../../../config/configDefault';
import { fetchExchangeRates } from '../../../ducks/exchangeRates.duck';
import { parse } from '../../../util/urlHelpers';
import algoliasearch from 'algoliasearch';
import { SUPPORTED_LANGUAGES, facetLabelMappings } from '../../../util/constants';
import { createBoundingBox, getCountryName } from '../../../util/data';
import { getAlgoliaIndexNameForLang } from '../../../util/urlHelpers';
import { fetchCurrentUser, setCUserCurrencyAndLocation } from '../../../ducks/user.duck';
import { loadAlgoliaInsights } from '../../../analytics/handlers';

// ================ Action types ================ //

export const SEARCH_MAP_SET_ACTIVE_LISTING =
  'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FACETS_REQUEST = 'app/SearchPage/FACETS_REQUEST';
export const FACETS_SUCCESS = 'app/SearchPage/FACETS_SUCCESS';
export const FACETS_ERROR = 'app/SearchPage/FACETS_ERROR';

export const ALGOLIA_QUERY_REQUEST = 'app/SearchPage/ALGOLIA_QUERY_REQUEST';
export const ALGOLIA_BACKGROUND_QUERY_REQUEST = 'app/SearchPage/ALGOLIA_BACKGROUND_QUERY_REQUEST';
export const ALGOLIA_QUERY_SUCCESS = 'app/SearchPage/ALGOLIA_QUERY_SUCCESS';
export const ALGOLIA_QUERY_ERROR = 'app/SearchPage/ALGOLIA_QUERY_ERROR';

export const ALGOLIA_QUERY_LISTINGS_SUCCESS = 'app/SearchPage/ALGOLIA_QUERY_LISTINGS_SUCCESS';
export const ALGOLIA_QUERY_STORES_SUCCESS = 'app/SearchPage/ALGOLIA_QUERY_STORES_SUCCESS';

export const SET_ALGOLIA_DEFAULT_SORT = 'app/SearchPage/SET_ALGOLIA_DEFAULT_SORT';


const ALGOLIA_CLIENT_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_API_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;
const ALGOLIA_LISTINGS_INDEX = getAlgoliaIndexNameForLang((typeof window !== 'undefined' && localStorage.getItem('appLanguage')) || 'en');
const ALGOLIA_USERS_INDEX = process.env.REACT_APP_ALGOLIA_USERS_INDEX;

const searchClient = ALGOLIA_CLIENT_ID && ALGOLIA_SEARCH_API_KEY
  ? algoliasearch(ALGOLIA_CLIENT_ID, ALGOLIA_SEARCH_API_KEY)
  : null;

const trackedSearchResultKeys = new Set();
const trackedNoResultsKeys = new Set();

const buildSearchTrackingKey = params => {
  if (!params) return '';
  const {
    query = '',
    filters = '',
    aroundLatLng = '',
    insideBoundingBox = '',
    city = '',
    address = '',
    country = '',
    sort = '',
  } = params;

  const parts = [
    `q:${String(query).trim().toLowerCase()}`,
    `f:${String(filters).trim().toLowerCase()}`,
    `latlng:${String(aroundLatLng).trim().toLowerCase()}`,
    `bbox:${Array.isArray(insideBoundingBox) ? insideBoundingBox.join(',') : String(insideBoundingBox).trim()}`,
    `city:${String(city).trim().toLowerCase()}`,
    `addr:${String(address).trim().toLowerCase()}`,
    `country:${String(country).trim().toLowerCase()}`,
    `sort:${String(sort).trim().toLowerCase()}`,
  ];

  return parts.join('|');
};

export function buildAlgoliaFilters(params) {
  const {
    category,
    access_to_rentals,
    Condition_of_the_item,
    Trust_and_verification,
    cancelation_policy,
    reason_for_listing,
    security_deposit,
    priceRange,
  } = params;

  let filtersArray = [];

  // Check each parameter and only add it to filtersArray if it has a value
  if (category) {
    filtersArray.push(`publicData.category:${category}`);
  }

  if (access_to_rentals) {
    filtersArray.push(`publicData.access_to_rentals:${access_to_rentals}`);
  }

  if (Condition_of_the_item) {
    filtersArray.push(`publicData.Condition_of_the_item:${Condition_of_the_item}`);
  }

  if (Trust_and_verification) {
    filtersArray.push(`publicData.Trust_and_verification:${Trust_and_verification}`);
  }

  if (cancelation_policy) {
    filtersArray.push(`publicData.cancelation_policy:${cancelation_policy}`);
  }

  if (reason_for_listing) {
    filtersArray.push(`publicData.reason_for_listing:${reason_for_listing}`);
  }

  if (security_deposit) {
    filtersArray.push(`publicData.security_deposit:${security_deposit}`);
  }

  if (priceRange) {
    const [from, to] = priceRange.split(',');
    filtersArray.push(`price.amount >= ${from} AND price.amount <= ${to}`);
  }

  // Join the filters with AND condition for Algolia
  return filtersArray.join(' AND ');
};

function getLabeledFacets(facets) {
  const labeledFacets = {};

  // Loop through each facet category in the facets object
  for (const facetCategory in facets) {

    // Remove "publicData." prefix if present
    const facetKey = facetCategory.replace("publicData.", "");

    // Find the correct label mapping for the facet key
    const labelMapping = facetLabelMappings[facetKey];

    if (labelMapping) {
      labeledFacets[facetKey] = Object.keys(facets[facetCategory]).map(facetValue => ({
        value: facetValue,
        label: labelMapping[facetValue] || facetValue, // Fallback to the raw value if no label is found
        count: facets[facetCategory][facetValue],
      }));
    } else {
      // If there's no mapping, just return the raw facet values and counts
      labeledFacets[facetKey] = Object.keys(facets[facetCategory]).map(facetValue => ({
        value: facetValue,
        label: facetValue, // Use raw value as label
        count: facets[facetCategory][facetValue],
      }));
    }
  }

  return labeledFacets;
}

const coverEarthRadius = 20037500;

// ================ Reducer ================ //

const initialState = {
  activeListingId: null,
  facets: [],
  facetsInProgress: false,
  facetsError: null,
  algoliaQueryInProgress: false,
  // The "full results" fetch that runs right after the quick first fetch. Tracked
  // so the UI can keep showing a loading state (never an error) while it — and
  // its retries — are still a chance to recover from a failed first fetch.
  algoliaBackgroundQueryInProgress: false,
  algoliaQueryError: null,
  algoliaListings: {
    relevant: [],
    irrelevant: []
  },
  algoliaStores: {
    relevant: [],
    irrelevant: []
  },
  nbHitsInsideBounds: 0,
  defaultSort: 'newest'
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case FACETS_REQUEST:
      return {
        ...state,
        facetsInProgress: true,
        facetsError: null,
      };
    case FACETS_SUCCESS:
      return {
        ...state,
        facetsInProgress: false,
        facets: payload,
      };
    case FACETS_ERROR:
      return {
        ...state,
        facetsInProgress: false,
        facetsError: payload
      };

    case ALGOLIA_QUERY_REQUEST:
      return {
        ...state,
        algoliaQueryInProgress: true,
        algoliaBackgroundQueryInProgress: false,
        algoliaQueryError: null,
      };
    case ALGOLIA_BACKGROUND_QUERY_REQUEST:
      return {
        ...state,
        algoliaBackgroundQueryInProgress: true,
      };
    case ALGOLIA_QUERY_SUCCESS:
      // Any successful query (foreground OR background) must clear a previous
      // error, otherwise the results area stays stuck on "failed to fetch
      // results!" even though fresh listings have arrived in the store.
      return {
        ...state,
        algoliaQueryInProgress: false,
        algoliaBackgroundQueryInProgress: false,
        algoliaQueryError: null,
      };
    case ALGOLIA_QUERY_LISTINGS_SUCCESS:
      return {
        ...state,
        algoliaQueryError: null,
        algoliaListings: {
          relevant: payload.listings,
          irrelevant: payload.irrelevantListings
        },
        nbHitsInsideBounds: payload.nbHitsInsideBounds
      };
    case ALGOLIA_QUERY_STORES_SUCCESS:
      return {
        ...state,
        algoliaQueryError: null,
        algoliaStores: {
          relevant: payload.stores,
          irrelevant: payload.irrelevantStores
        },
      }
    case ALGOLIA_QUERY_ERROR:
      // A failed first (foreground) fetch is always followed by the background
      // "full results" fetch, which is our recovery path. Keep the UI in a
      // loading state until that fetch (and its retries) also settle, so we never
      // flash an error the retry is about to fix. Only when the background fetch
      // itself fails is the error truly terminal.
      return {
        ...state,
        algoliaQueryInProgress: false,
        algoliaBackgroundQueryInProgress: !!payload?.expectBackgroundRetry,
        algoliaQueryError: payload,
      };

    case SET_ALGOLIA_DEFAULT_SORT:
      return {
        ...state,
        defaultSort: payload,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});


// Algolia facets
export const algoliaFacetsRequest = () => ({
  type: FACETS_REQUEST
});

export const algoliaFacetsSuccess = (facets) => ({
  type: FACETS_SUCCESS,
  payload: facets
});

export const algoliaFacetsError = (err) => ({
  type: FACETS_ERROR,
  payload: err
});


// Algolia Query
// Separate actions
const algoliaQueryListingsSuccess = ({ listings, nbHitsInsideBounds, irrelevantListings }) => ({
  type: ALGOLIA_QUERY_LISTINGS_SUCCESS,
  payload: { listings, nbHitsInsideBounds, irrelevantListings },
});

const algoliaQueryStoresSuccess = (stores) => ({
  type: ALGOLIA_QUERY_STORES_SUCCESS,
  payload: stores,
});

export const algoliaQueryRequest = () => ({
  type: ALGOLIA_QUERY_REQUEST
});

export const algoliaQuerySuccess = () => ({
  type: ALGOLIA_QUERY_SUCCESS,
});

export const algoliaBackgroundQueryRequest = () => ({
  type: ALGOLIA_BACKGROUND_QUERY_REQUEST,
});

// `expectBackgroundRetry` is true for the foreground fetch, whose failure is
// always followed by the background "full results" fetch — the UI stays loading
// until that settles instead of showing this error straight away.
export const algoliaQueryError = (context, err, { expectBackgroundRetry = false } = {}) => {
  const status = err?.response?.status ?? err?.status ?? null;
  // Report to Sentry (log.error falls back to console only when Sentry is not
  // configured). Previously the error object was passed as a 2nd arg the action
  // creator ignored, so every search failure was lost.
  if (err) {
    log.error(err, 'algolia-query-failed', { context, status });
  }
  return {
    type: ALGOLIA_QUERY_ERROR,
    payload: {
      context: typeof context === 'string' ? context : undefined,
      message:
        err?.response?.data?.error ||
        err?.message ||
        (typeof context === 'string' ? undefined : context?.message),
      status,
      expectBackgroundRetry,
    },
  };
};

export const setAlgoliaDefaultSort = (sort) => ({
  type: SET_ALGOLIA_DEFAULT_SORT,
  payload: sort,
});


export const getFacets = () => (dispatch, getState, sdk) => {
  dispatch(algoliaFacetsRequest());

  if (!searchClient) {
    const err = new Error('Algolia credentials are missing');
    dispatch(algoliaFacetsError(err));
    return Promise.resolve(null);
  }

  const locationPath = typeof window !== 'undefined' ? window.location.pathname : '';
  // Extract locale from URL (e.g., /fr/page), default to browser preference
  const language = SUPPORTED_LANGUAGES.find(
    code => code !== 'en' && (locationPath.startsWith(`/${code}/`) || locationPath === `/${code}`)
  ) || "en";

  const listingIndex = searchClient.initIndex(getAlgoliaIndexNameForLang(language));

  return listingIndex.search('', {
    facets: algoliaListingFacets, // Attribute to facet on
  })
    .then(({ facets }) => {
      const labeledFacets = getLabeledFacets(facets);
      dispatch(algoliaFacetsSuccess(labeledFacets));
      return facets;
    })
    .catch(err => {
      dispatch(algoliaFacetsError(err))
    });
};

export const getAlgoliaResults = (params, fetchedEntites, isBackgroundFetch = false) => (dispatch, getState, sdk) => {
  if (isBackgroundFetch) {
    dispatch(algoliaBackgroundQueryRequest());
  } else {
    dispatch(algoliaQueryRequest());
  }

  return queryAlgolia(params)
    .then(results => {
      const { listings, stores, nbHitsInsideBounds, farawayRelevantListings, irrelevantListings, irrelevantStores } = results.data;

      if (fetchedEntites.includes('listings')) {
        dispatch(algoliaQueryListingsSuccess({
          listings: listings,
          nbHitsInsideBounds: nbHitsInsideBounds,
          irrelevantListings: [...(farawayRelevantListings || []), ...(irrelevantListings || [])],
        }))
      }

      if (fetchedEntites.includes('stores')) {
        dispatch(algoliaQueryStoresSuccess({ stores, nbHitsInsideBounds, irrelevantStores }))
      }

      // Track search results view only for the foreground request (not background fetch)
      if (!isBackgroundFetch && fetchedEntites.includes('listings')) {
        const state = getState();
        const userToken = state?.user?.currentUser?.id?.uuid;
        if (userToken && typeof window !== 'undefined') {
          const trackingKey = buildSearchTrackingKey(params);
          if (trackingKey && !trackedSearchResultKeys.has(trackingKey)) {
            trackedSearchResultKeys.add(trackingKey);

            loadAlgoliaInsights().then(aa => {
              if (!aa || typeof aa !== 'function') return;

              const relevantListings = [...(listings || []), ...(farawayRelevantListings || [])];
              const objectIDs = relevantListings.map(hit => String(hit.objectID)).filter(Boolean);

              if (objectIDs.length) {
                const CHUNK_SIZE = 20;
                for (let i = 0; i < objectIDs.length; i += CHUNK_SIZE) {
                  const chunk = objectIDs.slice(i, i + CHUNK_SIZE);
                  aa('viewedObjectIDs', {
                    userToken,
                    eventName: 'Search Results Viewed',
                    index: ALGOLIA_LISTINGS_INDEX,
                    objectIDs: chunk,
                  });
                }
              } else {
                // No relevant results: emit a single "No Search Results" event with query + location + filters
                const normalizedQuery = String(params?.query || '').trim();
                const normalizedCity = String(params?.city || '').trim();

                const filtersPayload = [];
                if (params?.filters) {
                  String(params.filters)
                    .split(' AND ')
                    .map(f => f.trim())
                    .filter(Boolean)
                    .filter(f => !/^price\.amount\s*[<>]=?/i.test(f))
                    .forEach(f => filtersPayload.push(f));
                }
                if (normalizedCity) {
                  const needsQuotes = /\\s|:|\"/.test(normalizedCity);
                  const escapedCity = normalizedCity.replace(/\"/g, '\\"');
                  filtersPayload.push(`publicData.city:${needsQuotes ? `\"${escapedCity}\"` : escapedCity}`);
                }
                if (normalizedQuery) {
                  filtersPayload.push(`query:${normalizedQuery}`);
                }

                const uniqueFilters = Array.from(new Set(filtersPayload)).filter(Boolean);
                const noResultsKey = `${trackingKey}|no-results`;
                if (uniqueFilters.length && !trackedNoResultsKeys.has(noResultsKey)) {
                  trackedNoResultsKeys.add(noResultsKey);
                  aa('viewedFilters', {
                    userToken,
                    eventName: 'No Search Results',
                    index: ALGOLIA_LISTINGS_INDEX,
                    filters: uniqueFilters.slice(0, 20),
                  });
                }
              }
            });
          }
        }
      }

      dispatch(algoliaQuerySuccess());
      return results;
    })
    .catch(err => {
      // Foreground failure: keep the UI loading until the background fetch also
      // settles. Background failure: nothing left to try — surface it.
      dispatch(algoliaQueryError('error in getAlgoliaResults', err, {
        expectBackgroundRetry: !isBackgroundFetch,
      }));
    })
};

/**
 * Refine search results based on map viewport bounds
 * This is called when user pans/zooms the map
 * Preserves all existing filters, query, sort, etc. - only changes location params
 */
export const refineSearchByMapBounds = (viewportBounds, currentSearchParams) => (dispatch, getState, sdk) => {
  dispatch(algoliaQueryRequest());

  // Convert SDK LatLngBounds to Algolia insideBoundingBox format [north, east, south, west]
  const { ne, sw } = viewportBounds;
  const insideBoundingBox = [
    ne.lat,  // north
    ne.lng,  // east
    sw.lat,  // south
    sw.lng   // west
  ];

  // Preserve all original params except location-related ones
  // Remove aroundLatLng and country to use viewport bounds instead
  const { aroundLatLng, country, ...preservedParams } = currentSearchParams;

  // Override sort if it's 'nearest' - nearest sort doesn't make sense when exploring by map bounds
  // Map exploration should use field-based sorting (e.g., createdAt_desc) instead
  const effectiveSort = preservedParams.sort === 'nearest'
    ? 'createdAt_desc'
    : preservedParams.sort;

  const searchParams = {
    ...preservedParams,
    sort: effectiveSort,
    insideBoundingBox, // Use viewport bounds
    // aroundLatLng and country are omitted to allow cross-country exploration
  };

  return queryAlgolia(searchParams)
    .then(results => {
      const { listings, stores, nbHitsInsideBounds, farawayRelevantListings, irrelevantListings, irrelevantStores } = results.data;

      const fetchedEntites = currentSearchParams.fetchedEntites || ['listings'];

      if (fetchedEntites.includes('listings')) {
        dispatch(algoliaQueryListingsSuccess({
          listings: listings,
          nbHitsInsideBounds: nbHitsInsideBounds,
          irrelevantListings: [...(farawayRelevantListings || []), ...(irrelevantListings || [])],
        }));
      }

      if (fetchedEntites.includes('stores')) {
        dispatch(algoliaQueryStoresSuccess({ stores, nbHitsInsideBounds, irrelevantStores }));
      }

      dispatch(algoliaQuerySuccess());
      return results;
    })
    .catch(err => {
      dispatch(algoliaQueryError("error in refineSearchByMapBounds", err));
    });
};

export const loadData = (params, search, config) => async (
  dispatch,
  getState,
  sdk
) => {
  // Fetch exchange rates only if needed
  const { exchangeRates } = getState().exchangeRate;
  if (
    typeof window !== 'undefined' &&
    exchangeRates &&
    exchangeRates.length === 0
  ) {
    dispatch(fetchExchangeRates());
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const {
    page = 1,
    keyword = "",
    address,
    city: qCity,
    bounds,
    origin: latlng,
    category,
    access_to_rentals,
    Condition_of_the_item,
    Trust_and_verification,
    cancelation_policy,
    reason_for_listing,
    security_deposit,
    priceRange,
    sort,
    country: qCountry,
  } = queryParams;

  const hasQueryLocation = latlng?.lat && latlng?.lng && qCountry;

  // Get user location or use default
  let { currentUserLocationInfo } = getState().user;
  let locationInfo = currentUserLocationInfo;

  if (!locationInfo || !locationInfo?.origin) {
    try {
      const currentUser = await dispatch(fetchCurrentUser());
      const { currentUserLocationInfo } = await dispatch(setCUserCurrencyAndLocation(currentUser));
      locationInfo = currentUserLocationInfo;
    } catch (error) {
      console.error('Error fetching user location info:', error);
    }
  };

  const { origin: userOrigin, country: userCountryCode, userCity } = locationInfo || {};
  const { lng: userLng, lat: userLat } = userOrigin || {};
  const hasUserLocation = userLng && userLat && userCountryCode;

  // Get country from query param or user location — no hardcoded country fallback
  const country = (qCountry && latlng?.lat && latlng?.lng)
    ? qCountry
    : hasUserLocation ? getCountryName(userCountryCode) : null;
  const city = qCity || (hasUserLocation ? userCity : null);

  // Set up location parameters — null when no location so the server does a global search
  const aroundLatLng =
    latlng && latlng.lat && latlng.lng
      ? `${latlng.lat}, ${latlng.lng}`
      : hasUserLocation
        ? `${userLat}, ${userLng}`
        : null;

  const hasLocationAccess = hasQueryLocation || hasUserLocation;

  // Build filters for Algolia query
  const filters = buildAlgoliaFilters({
    category,
    access_to_rentals,
    Condition_of_the_item,
    Trust_and_verification,
    cancelation_policy,
    reason_for_listing,
    security_deposit,
    priceRange
  });

  // Set default sort (String(keyword) so numeric URL keywords still count as "has query")
  const defaultSort =
    hasLocationAccess &&
    (String(keyword ?? '').trim().length > 0 || filters?.length > 0)
      ? 'nearest'
      : 'createdAt_desc';
  dispatch(setAlgoliaDefaultSort(defaultSort));
  const sortBy = !!sort ? sort : defaultSort;
  const isNearestSort = sortBy === 'nearest';
  const isValidSort = config.search.sortConfig.options.some(option => option.key === sortBy);

  const locationPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const language = SUPPORTED_LANGUAGES.find(
    code => code !== 'en' && (locationPath.startsWith(`/${code}/`) || locationPath === `/${code}`)
  ) || "en";

  // Step 1: Fetch initial 12 listings for quick page load
  const langIndex = getAlgoliaIndexNameForLang(language);
  const indexName = isValidSort && !isNearestSort
    ? `${langIndex}_${sortBy}`
    : !isNearestSort ? `${langIndex}_${defaultSort}` : langIndex;

  const searchParams = {
    page,
    query: keyword == null || keyword === '' ? '' : String(keyword),
    address,
    city,
    aroundLatLng,
    filters,
    isNearestSort,
    country,
    sort: sortBy,
    minimumHits: 8,
    indexes: {
      listingIndex: { indexName }
    }
  };

  const fetchListings = dispatch(getAlgoliaResults(searchParams, ['listings']));
  const fetchFacets = dispatch(getFacets());

  return Promise.all([fetchListings, fetchFacets])
    .then(results => {
      // Start background fetch for full results after initial load
      dispatch(getAlgoliaResults({ ...searchParams, minimumHits: 50 }, ['listings'], true));
      return results;
    })
    .catch(err => {
      console.error('Error fetching initial results:', err);
    });
};
